import {
  BottomSheetScrollView,
  BottomSheetTextInput
} from '@gorhom/bottom-sheet';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { CheckIcon, CloseIcon } from '@/assets';
import { BottomModal } from '@/features/popup/components/BottomModal';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export const DESCRIPTION_MAX_LENGTH = 1000;

type Props = {
  visible: boolean;
  value: string;
  title: string;
  placeholder: string;
  maxLength: number;
  onConfirm: (nextValue: string) => void;
  onRequestClose: () => void;
};

export const MultilineEditorModal: FC<Props> = ({
  visible,
  value,
  title,
  placeholder,
  maxLength,
  onConfirm,
  onRequestClose
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit.descriptionEditor'
  });

  const [draft, setDraft] = useState(value);

  useEffect(() => {
    // Reset the draft to the caller's value whenever the modal (re)opens.
    // oxlint-disable-next-line react/set-state-in-effect
    if (visible) setDraft(value.slice(0, maxLength));
  }, [value, visible, maxLength]);

  return (
    <BottomModal
      isVisible={visible}
      title={title}
      titleAlign="left"
      onClose={onRequestClose}
      showCloseButton={false}
      keyboardShouldPersistTaps="handled"
      expandWithKeyboard
      scrollComponent={BottomSheetScrollView}
      endButtons={[
        {
          icon: CloseIcon,
          variant: 'close',
          accessibilityLabel: t('close'),
          shouldCloseWhenPressed: true
        },
        {
          icon: CheckIcon,
          variant: 'confirm',
          accessibilityLabel: t('confirm'),
          shouldCloseWhenPressed: true,
          onPress: () => onConfirm(draft)
        }
      ]}
    >
      <View style={styles.body}>
        <BottomSheetTextInput
          accessibilityLabel={title}
          accessibilityHint={title}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.onSurfaceVariant}
          value={draft}
          onChangeText={(text) => setDraft(text.slice(0, maxLength))}
          multiline
          maxLength={maxLength}
          style={[
            styles.input,
            { color: theme.colors.onSurface },
            // Web: suppress the browser's default focus outline so it doesn't
            // clash with our custom border.
            isWeb && ({ outlineStyle: 'none' } as unknown as TextStyle)
          ]}
          textAlignVertical="top"
        />
        <Text variant="bodySmall" style={styles.counter}>
          {t('counter', { count: draft.length, max: maxLength })}
        </Text>
      </View>
    </BottomModal>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    body: { paddingHorizontal: 24, gap: 8 },
    input: {
      height: 280,
      borderWidth: 1,
      borderColor: theme.colors.outlineVariant,
      borderRadius: 16,
      backgroundColor: theme.colors.surfaceContainerLow,
      padding: 16,
      fontSize: 16,
      lineHeight: 22
    },
    counter: { color: theme.colors.onSurfaceVariant, alignSelf: 'flex-end' }
  });
